/**
 * Shared constants for segment conditions preview components.
 */

// =============================================================================
// UI Constants
// =============================================================================

/** Maximum depth for rendering nested conditions (prevents infinite recursion) */
export const MAX_CONDITION_DEPTH = 10;

/** Maximum height for condition popovers in pixels */
export const POPOVER_MAX_HEIGHT = '350px';

/** Cache duration for condition details queries (5 minutes) */
export const CONDITION_CACHE_STALE_TIME = 5 * 60 * 1000;

// =============================================================================
// Condition Type Identifiers
// =============================================================================

/**
 * Backend condition type values.
 * Type 0 = Attribute condition
 * Type 1 = Event condition
 * Type 2 = Device attribute condition
 * Type 3 = Relationship condition (checked via relationship object in data)
 * Type 4 = Related condition (object relationships)
 * Type 5 = Object attribute condition (relationship_attr_filter for filtering on related object/relationship attributes)
 */
export const CONDITION_TYPE = {
  ATTRIBUTE: 0,
  EVENT: 1,
  DEVICE: 2,
  RELATIONSHIP: 3,
  RELATED: 4,
  OBJECT_ATTRIBUTE: 5,
} as const;

// =============================================================================
// Condition Data Types
// =============================================================================

/**
 * Related audit log entry containing condition data.
 */
export interface RelatedLogEntry {
  id: number;
  object_type: string;
  object_id: string;
  data: unknown;
  changed_to?: unknown;
  changed_from?: unknown;
}

/**
 * Attribute filter structure from backend.
 */
export interface AttributeFilter {
  field: string;
  operator: string;
  value?: unknown;
  inverse?: boolean;
}

/**
 * Event filter structure from backend.
 */
export interface EventFilter {
  etype?: number;
  times?: number;
  within?: number;
  inverse?: boolean;
  name?: string;
}

/**
 * Base condition structure with common fields.
 */
export interface BaseCondition {
  condition_id?: number;
  segment_id?: number;
  inverse?: boolean;
  type?: number | string;
}

/**
 * Attribute condition structure.
 */
export interface AttributeCondition extends BaseCondition {
  attribute_filter?: AttributeFilter;
  field?: string;
  attribute?: string;
  operator?: string;
  value?: unknown;
}

/**
 * Event condition structure.
 */
export interface EventCondition extends BaseCondition {
  event_filter?: EventFilter;
  event_type_id?: number;
  event?: {
    name?: string;
    type?: string;
    ui_type?: string;
  };
}

/**
 * Segment reference condition structure.
 */
export interface SegmentCondition extends BaseCondition {
  segment?: {
    id?: number;
    inverse?: boolean;
  };
}

/**
 * Group condition with nested children.
 */
export interface GroupCondition {
  and?: ConditionData[];
  or?: ConditionData[];
  not?: ConditionData | Record<string, unknown>;
}

/**
 * Union type for all condition data structures.
 */
export type ConditionData =
  | AttributeCondition
  | EventCondition
  | SegmentCondition
  | GroupCondition
  | Record<string, unknown>;

/**
 * Data source types for condition display with icons.
 * Imported from data-source-label.tsx as the single source of truth.
 */
import type { DataSourceType } from './data-source-label';
export type { DataSourceType };

/**
 * Parsed filter for event attribute conditions.
 */
export interface ParsedFilter {
  field: string;
  operator: string;
  value?: string;
  value2?: string;
}

/**
 * Parsed condition result for display.
 */
export interface ParsedCondition {
  type: string;
  prefix?: string;
  name?: string;
  operator?: string;
  value?: string;
  /** Secondary value for between operators */
  value2?: string;
  suffix?: string;
  /** Data source type for icon display */
  dataSourceType: DataSourceType;
  /** Icon name for message (e.g., 'email', 'sms', 'push') - shown before message name */
  messageIcon?: string;
  /** Icon name for campaign/parent (e.g., 'campaigns', 'newsletter', 'transactional-api') - shown before campaign name */
  campaignIcon?: string;
  /** Channel label (e.g., 'email', 'push notification', 'in-app message') - used for parsing display name */
  channelLabel?: string;
  /** Segment ID for segment conditions (enables popover preview) */
  segmentId?: number;
  /** Whether this is an inverse condition */
  inverse?: boolean;
  /** Fallback text if we can't parse the condition */
  fallback?: string;
  /** Event attribute filters (nested conditions) */
  filters?: ParsedFilter[];
  /** Operator for combining filters ("and" = matches all, "or" = matches any) */
  filtersOperator?: 'and' | 'or';
}
